import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  stepIndexFromDraftStep,
  useWizardStore,
  type BillingPeriod,
  type DeadlineDirection,
  type DeadlineMode,
  type Privacy,
  type SplitMode,
  type SubscriptionType,
  type WizardCurrency,
} from './wizardStore';
import { TypeStep } from './steps/TypeStep';
import { NameStep } from './steps/NameStep';
import { PriceStep } from './steps/PriceStep';
import { CollectStep } from './steps/CollectStep';
import { SplitStep } from './steps/SplitStep';
import { MembersStep } from './steps/MembersStep';
import { PreviewStep } from './steps/PreviewStep';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { subscriptionApi } from '@/entities/subscription/api';
import type { Subscription } from '@/entities/subscription/types';
import { useT } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

const STEPS = [TypeStep, NameStep, PriceStep, MembersStep, CollectStep, SplitStep, PreviewStep];
const TOTAL = STEPS.length;

// NameStep (index 1) manages its own internal scroll; outer area must be overflow-hidden
const SELF_SCROLL_STEPS = new Set([1]);

const variants = {
  enter: (dir: number) => ({ x: dir * 48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir * -48, opacity: 0 }),
};

function periodToWizard(period: Subscription['period']): {
  period: BillingPeriod;
  customInterval: number;
  customUnit: 'days' | 'weeks' | 'months';
} {
  if (period.unit === 'months' && period.interval === 1) {
    return { period: 'monthly', customInterval: 1, customUnit: 'months' };
  }
  if (period.unit === 'months' && period.interval === 3) {
    return { period: 'quarterly', customInterval: 3, customUnit: 'months' };
  }
  if (period.unit === 'months' && period.interval === 12) {
    return { period: 'yearly', customInterval: 12, customUnit: 'months' };
  }
  return {
    period: 'custom',
    customInterval: period.interval,
    customUnit: period.unit as 'days' | 'weeks' | 'months',
  };
}

function deadlineToWizard(shift: number): {
  deadlineMode: DeadlineMode;
  deadlineDays: number;
  deadlineDirection: DeadlineDirection;
} {
  if (shift === 0) {
    return { deadlineMode: 'same', deadlineDays: 3, deadlineDirection: 'before' };
  }
  if (shift === -1) {
    return { deadlineMode: 'before', deadlineDays: 3, deadlineDirection: 'before' };
  }
  return {
    deadlineMode: 'custom',
    deadlineDays: Math.max(1, Math.abs(shift)),
    deadlineDirection: shift < 0 ? 'before' : 'after',
  };
}

function subscriptionToWizardData(sub: Subscription) {
  const period = periodToWizard(sub.period);
  const deadline = deadlineToWizard(sub.deadline_shift);
  return {
    type: (sub.type === 'collection' ? 'collection' : 'subscription') as SubscriptionType,
    name: sub.name,
    icon: sub.icon,
    totalPrice: Number(sub.total_price) > 0 ? String(Number(sub.total_price)) : '',
    currency: sub.currency as WizardCurrency,
    ...period,
    billingDate: sub.billing_date ?? new Date().toISOString().slice(0, 10),
    ...deadline,
    splitMode: sub.split_mode as SplitMode,
    memberSlots: (sub.member_slots ?? []).map((slot, index) => ({
      id: `${index}-${slot.name}`,
      name: slot.name,
      amount: String(Number(slot.amount) || ''),
    })),
    privacy: sub.privacy as Privacy,
    showHistory: sub.show_history,
    subscriptionId: sub.id,
  };
}

export function CreateWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const [hydratedDraftId, setHydratedDraftId] = useState<string | null>(null);
  const { step, direction, prev, reset, hydrate, name, icon } = useWizardStore();
  const t = useT();

  const draftQ = useQuery({
    queryKey: ['subscription', draftId],
    queryFn: () => subscriptionApi.getDetail(draftId!),
    enabled: Boolean(draftId),
  });

  useEffect(() => {
    if (!draftId) {
      setHydratedDraftId(null);
      reset();
    }
  }, [draftId, reset]);

  useEffect(() => {
    if (!draftId || !draftQ.data) return;
    if (hydratedDraftId === draftId) return;
    const sub = draftQ.data.subscription;
    if (sub.status !== 'draft') {
      navigate(`/subscriptions/${sub.id}`, { replace: true });
      return;
    }
    hydrate(subscriptionToWizardData(sub), stepIndexFromDraftStep(sub.draft_step));
    setHydratedDraftId(draftId);
  }, [draftId, draftQ.data, hydratedDraftId, hydrate, navigate]);

  const handleBack = () => {
    if (step === 0) navigate(-1);
    else prev();
  };

  const handleClose = () => {
    navigate('/');
  };

  const StepComponent = STEPS[step];

  const autoLetter = name.trim() ? name.trim()[0].toUpperCase() : '';
  const isServiceSlug = icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(icon);
  const isEmoji = icon.length > 0 && !isServiceSlug;
  const showIcon = step > 1 && !!(name || icon);

  const selfScroll = SELF_SCROLL_STEPS.has(step);

  if (draftId && draftQ.isLoading) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden px-[18px] pt-4 gap-3 animate-pulse">
        <div className="h-[44px] rounded-[16px] bg-white/40" />
        <div className="h-[6px] rounded-full bg-black/[0.07]" />
        <div className="h-[140px] rounded-[20px] bg-white/35 mt-2" />
        <div className="h-[52px] rounded-[18px] bg-white/35 mt-auto mb-3" />
      </div>
    );
  }

  if (draftId && draftQ.isError) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center px-[24px] text-center">
        <p className="text-[16px] font-semibold text-text-primary">{t('wizard.draftLoadError')}</p>
        <button
          className="mt-3 text-[14px] font-semibold text-brand-green"
          onClick={() => draftQ.refetch()}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-[18px] pt-2 pb-3 flex-shrink-0">
        <button
          onClick={handleBack}
          className="text-text-secondary -ml-1 flex-shrink-0 w-[44px] h-[44px] flex items-center justify-center"
          aria-label={t('common.back')}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        <div className="flex-1 flex items-center gap-2.5 min-w-0">
          {showIcon && (
            <span className="flex-shrink-0">
              {isEmoji ? (
                <span className="w-[30px] h-[30px] rounded-[8px] bg-brand-gradient flex items-center justify-center text-[16px] shadow-[0_3px_8px_rgba(18,120,90,0.28)] leading-none">
                  {icon}
                </span>
              ) : isServiceSlug ? (
                <img
                  src={`/api/v1/icons/${icon}/default`}
                  alt={icon}
                  className="w-[30px] h-[30px] rounded-[8px] object-contain bg-white/60 p-0.5"
                />
              ) : (
                <SubscriptionIcon letter={autoLetter} size="sm" />
              )}
            </span>
          )}
          <b className="text-[18px] truncate">
            {step === 0 ? t('wizard.new') : name || t('wizard.new')}
          </b>
        </div>

        <span className="text-[13px] text-text-secondary font-mono flex-shrink-0">
          {t('wizard.stepOf', { step: String(step + 1), total: String(TOTAL) })}
        </span>

        <button
          onClick={handleClose}
          className="text-text-secondary -mr-1 flex-shrink-0 w-[44px] h-[44px] flex items-center justify-center"
          aria-label={t('common.close')}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-[6px] mx-5 mb-2 rounded-full bg-black/[0.07] overflow-hidden flex-shrink-0">
        <motion.div
          className="h-full rounded-full bg-brand-gradient"
          animate={{ width: `${((step + 1) / TOTAL) * 100}%` }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        />
      </div>

      {/*
        Footer portal target is rendered BEFORE the scroll area in DOM order
        so the portal container exists when step components mount and call WizardFooter.
        `order-last` moves it visually to the bottom of the flex column.
      */}
      <div className="order-last flex-shrink-0 px-4 pt-3 pb-3">
        <div id="wizard-footer-portal" className="flex flex-col gap-2" />
      </div>

      {/* Step content */}
      <div className={cn(
        'flex-1 overflow-x-hidden px-[18px]',
        selfScroll ? 'overflow-hidden flex flex-col' : 'overflow-y-auto liquid-scrollbar',
      )}>
        {selfScroll ? (
          <div className="flex flex-col pt-2 w-full min-w-0 max-w-full flex-1 min-h-0">
            <StepComponent />
          </div>
        ) : (
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={step}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.18, ease: 'easeInOut' }}
              className="flex flex-col pt-2 w-full min-w-0 max-w-full min-h-full pb-2"
            >
              <StepComponent />
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
